import React from 'react'
import { Body } from '../Shell/Shell'
import {
  GET_SIGNATURES_BY_GROUP_TOKEN,
  SET_SIGNER_EMAIL_BY_TOKEN,
  RESEND_SIGNER_EMAIL,
} from 'requests'
import MessageCard from '../Shell/MessageCard'
import UserIcon from '@tecuity/sos-icons/react/user-square-solid'
import CheckIcon from '@tecuity/sos-icons/react/check'
import PencilIcon from '@tecuity/sos-icons/react/pencil-solid'
import { Spinner } from '../Loaders/Loaders'
import Portal from 'components/Portal/Portal'
import { ConfirmDialog } from 'components/Modal/Modal'
import { VALIDATE } from 'components/Wizard2/validate'
import ReactMarkdown from '../Markdown/Markdown'
import './Signatures.scss'
import { format } from 'date-fns'
import { setErrorToast, setSuccessToast } from '../../actions'
import { connect } from 'react-redux'

const EMAIL_ERROR = 'The email you provided was not a valid email. Please try again.'
const SUCCESS_MESSAGE = `Your document was successfully sent for signatures. \n\nEach signer will receive an email requesting their signature. When all the signers have signed you will receive an email with instructions to complete your filing.\n\nYou may return to this page anytime to view the status of each signer.`
const ADD_REMOVE_MESSAGE = `Adding or removing signers will require any signers who have previously signed this document, to re-sign.\n\n Are you sure you wish to add or remove signers?`

const ManageSignaturesPage = ({
  location: { state: locationState = {} },
  match: {
    params: { groupToken },
  },
  history,
}) => {
  const [loading, setLoading] = React.useState(false)
  const [signersLoading, setSignersLoading] = React.useState(true)
  const [confirmingCorrection, setConfirmingCorrection] = React.useState(false)
  const [amendmentNum, setAmendmentNum] = React.useState('')
  const [formDefinitionId, setFormDefinitionId] = React.useState('')
  const [showAddRemove, setShowAddRemove] = React.useState(true)

  const startCorrection = () => {
    history.push('/correct/' + formDefinitionId + '/' + amendmentNum)
  }

  return (
    <Body
      className="receipt-wrapper flex-center-page manage-signers-page"
      backgroundThemeColor="light-neutral"
    >
      {locationState.showSuccessMessage && !signersLoading && (
        <MessageCard
          title={
            <span className="signer-success-title">
              <div className="signer-success-icon">
                <CheckIcon />
              </div>
              Successfully Sent
            </span>
          }
          content={SUCCESS_MESSAGE}
          className="signatures-success-message animated fadeInDown"
        />
      )}
      <MessageCard
        style={{ display: signersLoading ? 'none' : '' }}
        className={`animated ${signersLoading ? '' : 'fadeInUp'}`}
        title={
          <span className="signers-manage-title">
            Manage Signatures{' '}
            {loading && (
              <Spinner className="animated fadeIn" themeColor="medium-neutral" size="small" />
            )}
          </span>
        }
        // content={``}
      >
        <ConnectedSignersManager
          groupToken={groupToken}
          setFormDefinitionId={setFormDefinitionId}
          setAmendmentNum={setAmendmentNum}
          setShowAddRemove={setShowAddRemove}
          onLoadStarted={() => setLoading(true)}
          onLoadEnded={() => setLoading(false)}
          onInitialized={() => setSignersLoading(false)}
          disabled={loading}
        />
        {showAddRemove && (
          <div className="flex-row add-wrapper">
            <button className="btn btn-neutral" onClick={() => setConfirmingCorrection(true)}>
              Add or Remove Signers
            </button>
          </div>
        )}
      </MessageCard>
      <Portal isOpened={confirmingCorrection}>
        <ConfirmDialog
          closeModal={() => setConfirmingCorrection(false)}
          leftAligned
          onModalClosed={() => {}}
          message={<ReactMarkdown source={ADD_REMOVE_MESSAGE} className="markdown" />}
          buttons={[
            {
              label: 'Yes',
              onClick: startCorrection,
              className: 'btn-primary',
            },
            { label: 'No', onClick: () => {}, className: 'btn-default' },
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
    </Body>
  )
}

export default ManageSignaturesPage

const SignersManager = ({
  groupToken,
  onLoadStarted,
  onLoadEnded,
  onInitialized,
  disabled,
  setFormDefinitionId,
  setAmendmentNum,
  setShowAddRemove,
  setErrorToast,
  setSuccessToast
}) => {
  const [signers, setSigners] = React.useState(null)
  const [initialLoading, setInitialLoading] = React.useState(true)

  const handleErrorToastRequested = errorMessage => {
    setErrorToast(errorMessage)
  }

  const handleSuccessToastRequested = message => {
    setSuccessToast(message)
  }

  React.useEffect(() => {
    GET_SIGNATURES_BY_GROUP_TOKEN(
      groupToken,
      (signers) => {
        setSigners(signers)
        setInitialLoading(false)
        onInitialized()
        setFormDefinitionId(signers[0].FORM_DEFINITION_ID)
        setAmendmentNum(signers[0].SOURCE_NUM)
        setShowAddRemove(
          process.env.CONTROL_ADD_OR_REMOVE_SIGNERS ? signers[0].CAN_ADD_OR_REMOVE_YN : true
        )
      },
      (err) => {
        console.error(err)
        setInitialLoading(false)
        onInitialized()
      }
    )
  }, [groupToken])

  return (
    <div className="signers-display-wrapper">
      {!initialLoading ? (
        signers ? (
          signers.map((signer) => (
            <SignerRow
              name={signer.SIGNER_NAME}
              email={signer.EMAIL}
              hasSigned={!!signer.SIGNED_DATE}
              signedDate={format(new Date(signer.SIGNED_DATE), 'MM/dd/yyyy')}
              token={signer.TOKEN}
              onErrorToastRequested={handleErrorToastRequested}
              onSuccessToastRequested={handleSuccessToastRequested}
              onLoadStarted={onLoadStarted}
              onLoadEnded={onLoadEnded}
              disabled={disabled}
              key={signer.TOKEN}
            />
          ))
        ) : (
          <p className="danger-text">
            Something went wrong while loading this page. Please try again later.
          </p>
        )
      ) : (
        <div className="loader-wrapper">
          <Spinner themeColor="neutral" />
        </div>
      )}
    </div>
  )
}

const ConnectedSignersManager = connect(
  null,
  {
    setErrorToast,
    setSuccessToast
  }
)(SignersManager)

const SignerRow = ({
  name,
  email,
  hasSigned,
  signedDate,
  token,
  disabled,
  onErrorToastRequested,
  onSuccessToastRequested,
  onLoadStarted,
  onLoadEnded,
}) => {
  const [editingEmail, setEditingEmail] = React.useState(false)
  const [optimisticEmail, setOptimisticEmail] = React.useState(null)
  const [didResend, setDidResend] = React.useState(false)
  const inputRef = React.useRef()

  React.useEffect(() => {
    if (editingEmail) {
      inputRef.current.select()
    }
  }, [editingEmail])

  const startEmailEdit = () => setEditingEmail(true)

  const handleBlur = () => {
    const newEmail = inputRef.current.value
    if (!newEmail) {
      setEditingEmail(false)
      onErrorToastRequested(EMAIL_ERROR)
    } else if (newEmail === (optimisticEmail || email)) {
      setEditingEmail(false)
    } else {
      VALIDATE.isValidEmailFormat(newEmail, { type: 'text' }, {}, (isValid) => {
        if (isValid) {
          onLoadStarted()
          setOptimisticEmail(newEmail)
          setEditingEmail(false)
          SET_SIGNER_EMAIL_BY_TOKEN(
            { SIGNER_EMAIL: newEmail, TOKEN: token },
            () => {
              onLoadEnded()
              onSuccessToastRequested(`New email for ${name} successfully saved`)
            },
            (err) => {
              console.error(err)
              onLoadEnded()
              setOptimisticEmail(null)
              onErrorToastRequested(
                'Something went wrong while updating this email. Please try again later.'
              )
            }
          )
        } else {
          onErrorToastRequested(EMAIL_ERROR)
          setEditingEmail(false)
        }
      })
    }
  }

  const checkEnter = (e) => {
    if (e.keyCode === 13) {
      handleBlur()
    }
  }

  const resendEmail = () => {
    onLoadStarted()
    RESEND_SIGNER_EMAIL(
      token,
      () => {
        setDidResend(true)
        onLoadEnded()
      },
      (err) => {
        console.error(err)
        onLoadEnded()
        onErrorToastRequested('The email could not be sent at this time, please try again later.')
      }
    )
  }

  return (
    <div className={`signer-display-row flex-row ${hasSigned ? 'signed' : ''}`}>
      <div className={`signer-icon`}>{hasSigned ? <CheckIcon /> : <UserIcon />}</div>
      <div className="flex-column signer-info">
        <span>{name}</span>
        {editingEmail ? (
          <input
            type="text"
            defaultValue={optimisticEmail || email}
            onBlur={handleBlur}
            onKeyDown={checkEnter}
            autoFocus
            ref={inputRef}
          />
        ) : (
          <span
            className={hasSigned || disabled ? 'disabled' : ''}
            role={!hasSigned && !disabled ? 'button' : ''}
            tabIndex={!hasSigned && !disabled ? 0 : undefined}
            onClick={hasSigned || disabled ? undefined : startEmailEdit}
            onKeyDown={(e) => {
              if (!disabled && !hasSigned && (e.keyCode === 13 || e.keyCode === 32)) {
                startEmailEdit()
              }
            }}
          >
            {optimisticEmail || email} <PencilIcon />
          </span>
        )}
      </div>
      {!hasSigned ? (
        <button disabled={disabled || didResend} className="btn btn-default" onClick={resendEmail}>
          {didResend ? 'Resent' : 'Resend'}
        </button>
      ) : (
        <div className="signed-date">
          <span className="label">Signed On:</span>
          <br />
          <span>{signedDate}</span>
        </div>
      )}
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/Signatures/ManageSignatures.js